import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  RoleSummary,
  UserStatus,
  UserSummary,
} from "@yazamco/shared-types";
import { Icon } from "../../../components/ui/Icon";
import { useToast } from "../../../components/feedback/Toast";
import { useAuth } from "../../../auth/AuthContext";
import {
  assignUserRole,
  deleteUser,
  fetchRoles,
  fetchUsers,
  inviteUser,
  revokeUserRole,
  setUserStatus,
} from "../../../lib/users";

const usersKey = ["settings", "users"] as const;
const rolesKey = ["settings", "roles"] as const;

/** Initials for the avatar, from whatever the directory gave us. */
function initials(name: string | null, email: string): string {
  const source = (name ?? "").trim() || email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0] ?? "");
  return (letters.join("") || source.slice(0, 2)).toUpperCase();
}

const STATUS_BADGE: Record<UserStatus, { className: string; label: string }> = {
  active: { className: "badge badge-success", label: "Active" },
  invited: { className: "badge badge-info", label: "Invited" },
  suspended: { className: "badge badge-warning", label: "Suspended" },
  disabled: { className: "badge badge-danger", label: "Disabled" },
  deleted: { className: "badge badge-neutral", label: "Deleted" },
};

/**
 * Least to most privileged, so the slider reads left to right the way the
 * legend beneath it does.
 *
 * Keyed on the built-in role KEYS rather than on anything derived: a role's
 * authority is its grants, and no ordering can be computed from a name. A
 * custom role is not in the map and sorts after the built-ins, alphabetically —
 * the honest answer when the deployment has invented a rank this cannot place.
 */
const ROLE_RANK: Record<string, number> = {
  read_only: 0,
  auditor: 1,
  operator: 2,
  admin: 3,
  super_admin: 4,
};

function byPrivilege(a: RoleSummary, b: RoleSummary): number {
  const ra = ROLE_RANK[a.key] ?? 99;
  const rb = ROLE_RANK[b.key] ?? 99;
  return ra === rb ? a.name.localeCompare(b.name) : ra - rb;
}

const ROLE_LABEL: Record<string, string> = {
  read_only: "Read only",
  auditor: "Auditor",
  operator: "Operator",
  admin: "Admin",
  super_admin: "Super admin",
};

/** The canvas uses short labels inside the compact segmented control. */
function roleLabel(role: RoleSummary): string {
  return ROLE_LABEL[role.key] ?? role.name;
}

interface Props {
  /** `authorization.assign_roles` — without it the roles are read-only. */
  canManage: boolean;
  /** `users.create` — invite an address ahead of a first sign-in. */
  canInvite: boolean;
  /** `users.disable` — suspend and reactivate. */
  canSuspend: boolean;
  /** `users.delete` — remove the account for good. */
  canRemove: boolean;
}

/**
 * Users & roles — the deployment's directory.
 *
 * ── What a row has to make obvious ──────────────────────────────────────────
 *
 * A user's access comes from their ROLE ASSIGNMENTS and from nothing else: the
 * `users.role` column is a leftover of the flat model that no request reads.
 * An account with no role therefore signs in perfectly well and is then refused
 * by every module — the failure that looks, to the person, like a broken login,
 * and to the administrator like a user who does not exist.
 *
 * So the control is the canvas's segmented slider, and "No access" is its
 * LEFTMOST POSITION rather than a separate warning beside it. That is the whole
 * point of using a slider here: the stranded state stops being an absence you
 * have to notice and becomes a position you can see the row sitting in, one
 * click away from the fix. It is drawn in the danger colour when selected,
 * because unlike every other position it is a fault rather than a setting.
 *
 * The slider is single-choice while `user_roles` is a join table, so choosing a
 * role revokes the others (see `setRole`). A user who somehow holds two — by
 * invitation, or by SQL — cannot be drawn on it honestly, so that row falls
 * back to listing them and says why. Showing one of the two and hiding the
 * other would misreport what the person can actually do.
 *
 * Nothing here is a security boundary: `canManage` hides controls the caller
 * would be refused anyway, and the API re-decides every request.
 */
export function UsersRolesTab({
  canManage,
  canInvite,
  canSuspend,
  canRemove,
}: Props): JSX.Element {
  const { user: me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("");

  const users = useQuery({
    queryKey: usersKey,
    queryFn: fetchUsers,
    staleTime: 15_000,
  });
  const roles = useQuery({
    queryKey: rolesKey,
    queryFn: fetchRoles,
    staleTime: 5 * 60_000,
  });

  /**
   * Every mutation invalidates the directory, and nothing tries to patch a row
   * in place. A role change alters the chips, the "No access" warning and — for
   * the caller's own row — what the rest of the application will let them
   * reach; re-reading the list is one request and cannot disagree with the
   * server about any of it.
   */
  function onSettled(): void {
    void queryClient.invalidateQueries({ queryKey: usersKey });
    void queryClient.invalidateQueries({ queryKey: rolesKey });
  }

  function onError(error: unknown): void {
    toast.error(
      error instanceof Error ? error.message : "The change did not go through.",
    );
  }

  /**
   * Move a user to one role — what a position on the slider means.
   *
   * The control is single-choice, so picking a role has to END the others;
   * leaving them assigned would show a user on "Operator" while they still
   * carried Administrator, which is the one thing a slider must never do.
   *
   * ASSIGN FIRST, then revoke. Two reasons, and both matter:
   *
   *   - the account never passes through a state with no role, so a failure
   *     halfway leaves the user over-privileged rather than locked out;
   *   - it keeps the server's last-super-admin guard meaningful. Demoting the
   *     only Super Administrator is refused on the REVOKE, which is correct —
   *     granting them Read Only as well does not give the deployment a second
   *     administrator.
   */
  const setRole = useMutation({
    mutationFn: async ({
      user,
      roleId,
    }: {
      user: UserSummary;
      roleId: string;
    }) => {
      if (roleId && !user.roles.some((role) => role.id === roleId)) {
        await assignUserRole(user.id, roleId);
      }
      for (const held of user.roles) {
        if (held.id !== roleId) await revokeUserRole(user.id, held.id);
      }
    },
    onSuccess: (_result, { roleId }) =>
      toast.success(
        roleId
          ? `Role set to ${roleList.find((role) => role.id === roleId)?.name ?? "the new role"}.`
          : "Access removed.",
      ),
    onError,
    onSettled,
  });

  const changeStatus = useMutation({
    mutationFn: ({
      user,
      status,
    }: {
      user: UserSummary;
      status: "active" | "suspended";
    }) => setUserStatus(user.id, status, user.version),
    onSuccess: (_result, variables) =>
      toast.success(
        variables.status === "active"
          ? "Account reactivated."
          : "Account suspended.",
      ),
    onError,
    onSettled,
  });

  const remove = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => toast.success("User removed."),
    onError,
    onSettled,
  });

  const invite = useMutation({
    mutationFn: () =>
      inviteUser({
        email: inviteEmail.trim(),
        roleIds: inviteRole ? [inviteRole] : [],
      }),
    onSuccess: () => {
      toast.success(`Invited ${inviteEmail.trim()}.`);
      setInviting(false);
      setInviteEmail("");
      setInviteRole("");
    },
    onError,
    onSettled,
  });

  const roleList: RoleSummary[] = (roles.data?.roles ?? [])
    .filter((role) => role.isEnabled)
    .sort(byPrivilege);
  const items = users.data?.items ?? [];
  const busy =
    setRole.isPending ||
    changeStatus.isPending ||
    remove.isPending ||
    invite.isPending;

  return (
    <section className="set-section" data-testid="users-roles-tab">
      <div className="set-section-head">
        <div>
          <h2>Users &amp; roles</h2>
          <p>
            Roles govern administration only. What Copilot may read is always
            the user&rsquo;s own Microsoft 365 permissions.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setInviting((open) => !open)}
          disabled={!canInvite}
          title={
            canInvite
              ? "Create an account ahead of somebody's first sign-in"
              : "Inviting a user needs the users.create permission."
          }
          data-testid="users-invite"
        >
          <Icon name="plus" size={14} stroke={2.1} />
          Invite user
        </button>
      </div>

      {/* An invitation creates the row before the person has ever signed in, so
          their first Entra sign-in links to it and lands on the role chosen
          here instead of on whatever the deployment's default happens to be. */}
      {inviting && canInvite && (
        <form
          className="set-invite"
          data-testid="users-invite-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (inviteEmail.trim()) invite.mutate();
          }}
        >
          <label className="field">
            <span className="field-label">Email address</span>
            <input
              type="email"
              required
              value={inviteEmail}
              placeholder="person@yazamco.pro"
              onChange={(event) => setInviteEmail(event.target.value)}
              data-testid="users-invite-email"
            />
          </label>
          <label className="field">
            <span className="field-label">Starting role</span>
            <select
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value)}
              data-testid="users-invite-role"
            >
              <option value="">No role — approve later</option>
              {roleList.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>
          <div className="set-invite-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Send invitation
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setInviting(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <p className="set-role-legend">
        {roleList.length > 0 ? (
          roleList.map((role, index) => (
            <span key={role.id}>
              {index > 0 && " · "}
              <strong>{role.name}</strong>{" "}
              {role.description ?? "No description."}
            </span>
          ))
        ) : (
          <>Roles are loading.</>
        )}
      </p>

      <div className="set-list">
        {users.isLoading && (
          <div className="set-list-empty">Loading the directory&hellip;</div>
        )}

        {users.isError && (
          <div className="set-list-empty">
            <p className="field-error" role="alert">
              {users.error instanceof Error
                ? users.error.message
                : "The directory could not be loaded."}
            </p>
          </div>
        )}

        {users.isSuccess && items.length === 0 && (
          <div className="set-list-empty">
            Nobody has signed in to this deployment yet.
          </div>
        )}

        {items.map((user) => {
          const isSelf = me?.email.toLowerCase() === user.email.toLowerCase();
          const status = STATUS_BADGE[user.status];
          // Exactly one role is what the slider can represent. Zero is "No
          // access"; two or more is the fallback below.
          const single = user.roles.length <= 1;
          const selected = user.roles[0]?.id ?? "";
          const stranded = user.roles.length === 0 && user.status !== "invited";

          return (
            <div
              className="set-row set-user-row"
              key={user.id}
              data-testid={`users-row-${user.id}`}
            >
              <span className="set-avatar" aria-hidden>
                {initials(user.displayName, user.email)}
              </span>

              <div className="set-user-copy">
                <strong>
                  {user.displayName || user.email}
                  {isSelf && <span className="badge badge-neutral">You</span>}
                  {user.status !== "active" && (
                    <span className={status.className}>{status.label}</span>
                  )}
                </strong>
                <span className="set-user-mail">{user.email}</span>
              </div>

              {single ? (
                <div
                  className="segmented set-role-picker"
                  role="radiogroup"
                  aria-label={`Role for ${user.email}`}
                  data-stranded={stranded ? "true" : undefined}
                  data-testid={`users-role-picker-${user.id}`}
                >
                  {/* Leftmost, and deliberately part of the same control:
                      removing somebody's access is the same decision as
                      choosing it, taken in the other direction. */}
                  <button
                    type="button"
                    role="radio"
                    className="segmented-option set-role-off"
                    aria-checked={selected === ""}
                    disabled={!canManage || busy}
                    title={
                      canManage
                        ? "Signs in, and is refused by every module"
                        : "Changing a role needs the authorization.assign_roles permission."
                    }
                    onClick={() => {
                      if (selected !== "") setRole.mutate({ user, roleId: "" });
                    }}
                  >
                    No access
                  </button>
                  {roleList.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      role="radio"
                      className="segmented-option"
                      aria-checked={selected === role.id}
                      disabled={!canManage || busy}
                      title={role.description ?? role.name}
                      onClick={() => {
                        if (selected !== role.id) {
                          setRole.mutate({ user, roleId: role.id });
                        }
                      }}
                    >
                      {roleLabel(role)}
                    </button>
                  ))}
                </div>
              ) : (
                <div
                  className="set-user-roles"
                  data-testid={`users-roles-multi-${user.id}`}
                >
                  {user.roles.map((role) => (
                    <span className="set-role-chip" key={role.id}>
                      {ROLE_LABEL[role.key] ?? role.name}
                    </span>
                  ))}
                  {canManage && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      title="Replaces both with a single role"
                      onClick={() =>
                        setRole.mutate({ user, roleId: user.roles[0]!.id })
                      }
                    >
                      Reduce to one role
                    </button>
                  )}
                </div>
              )}

              {/* Keep both actions in every row so the canvas does not shift.
                  The API still decides what is allowed; actions unavailable
                  for this principal or this user remain visible in grey. */}
              <div className="set-row-actions" aria-label={`Actions for ${user.email}`}>
                <button
                  type="button"
                  className="icon-btn icon-btn-ghost"
                  disabled={
                    busy ||
                    !canSuspend ||
                    isSelf ||
                    user.status === "invited" ||
                    user.status === "deleted"
                  }
                  aria-label={
                    user.status === "active"
                      ? `Suspend ${user.email}`
                      : `Reactivate ${user.email}`
                  }
                  title={
                    isSelf
                      ? "You cannot suspend your own account"
                      : !canSuspend
                        ? "Suspending a user needs the users.disable permission"
                        : user.status === "active"
                          ? "Suspend the account and end every live session"
                          : user.status === "invited" || user.status === "deleted"
                            ? "This account cannot be suspended"
                            : "Reactivate the account"
                  }
                  onClick={() =>
                    changeStatus.mutate({
                      user,
                      status: user.status === "active" ? "suspended" : "active",
                    })
                  }
                >
                  <Icon
                    name={user.status === "active" ? "lock" : "refresh"}
                    size={15}
                  />
                </button>

                <button
                  type="button"
                  className="icon-btn icon-btn-ghost icon-btn-danger"
                  disabled={busy || !canRemove || isSelf || user.status === "deleted"}
                  aria-label={`Remove ${user.email}`}
                  title={
                    isSelf
                      ? "You cannot remove your own account"
                      : !canRemove
                        ? "Removing a user needs the users.delete permission"
                        : user.status === "deleted"
                          ? "This account has already been removed"
                          : "Remove user"
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove ${user.email}? This deletes the account and every ` +
                          "session it holds. They can sign in again only if the " +
                          "deployment provisions new users automatically.",
                      )
                    ) {
                      remove.mutate(user.id);
                    }
                  }}
                >
                  <Icon name="trash" size={15} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="set-role-legend">
        A person who has never signed in does not appear here until you invite
        them. Everyone else was created on their first Microsoft sign-in and
        holds whatever <code>ENTRA_DEFAULT_ROLE</code> grants.
      </p>
    </section>
  );
}
